import classNames from 'classnames';
import { uniqueId } from 'lodash';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import { FormGroup, Input, Label, UncontrolledTooltip } from 'reactstrap';

import FormField from '../../../models/FormField';
import { modelOf } from '../../../prop-types';
import Icon from '../../common/Icon';
import Field from '../Field';
import CustomFormField from '../../../models/CustomFormField';
import FieldError from '../FieldError';
import FloatingLabelWrapper from '../FloatingLabelWrapper';
import { hasFloatingValue } from '../floatingLabelUtils';

const BASE_CLASS = 'FormGroupField';

const renderLabelWithExtras = (id, label, required, tooltip) => (
  <>
    {label}
    {required && <span className={`${BASE_CLASS}__required-mark`}>*</span>}
    {tooltip && (
      <>
        <Icon name="info" className={`tooltip-${id}`} />
        <UncontrolledTooltip target={`.tooltip-${id}`}>
          {tooltip}
        </UncontrolledTooltip>
      </>
    )}
  </>
);

const FormGroupField = ({
  label,
  fieldName,
  field,
  type,
  hideLabel,
  required,
  tooltip,
  description,
  floatingLabel,
  formName,
  customError,
  formGroupClassName,
  ...rest
}) => {
  const id = uniqueId(`${formName}__${fieldName}--`);
  const useFloating = floatingLabel && !hideLabel && label != null;
  const hasValue = hasFloatingValue(field, {
    excludeTypes: ['checkbox', 'radio'],
  });

  if (useFloating) {
    return (
      <FormGroup
        className={classNames(
          BASE_CLASS,
          `${BASE_CLASS}--floating`,
          hasValue && `${BASE_CLASS}--has-value`,
          formGroupClassName
        )}
      >
        <FloatingLabelWrapper
          baseClass={BASE_CLASS}
          id={id}
          label={label}
          required={required}
          tooltip={tooltip}
        >
          <Field
            component={Input}
            field={field}
            type={type}
            name={fieldName}
            id={id}
            required={required}
            {...rest}
          />
        </FloatingLabelWrapper>
        {description}
        <FieldError field={field} customError={customError} />
      </FormGroup>
    );
  }

  return (
    <FormGroup className={classNames(BASE_CLASS, formGroupClassName)}>
      <Label for={id} hidden={hideLabel}>
        {renderLabelWithExtras(id, label, required, tooltip)}
      </Label>
      <Field
        component={Input}
        field={field}
        type={type}
        name={fieldName}
        id={id}
        required={required}
        {...rest}
      />
      {description}
      <FieldError field={field} customError={customError} />
    </FormGroup>
  );
};

FormGroupField.propTypes = {
  field: PropTypes.oneOfType([modelOf(FormField), modelOf(CustomFormField)])
    .isRequired,
  formName: PropTypes.string.isRequired,
  fieldName: PropTypes.string.isRequired,
  label: PropTypes.node,
  formGroupClassName: PropTypes.string,
  type: PropTypes.string,
  tooltip: PropTypes.string,
  hideLabel: PropTypes.bool,
  required: PropTypes.bool,
  description: PropTypes.node,
  floatingLabel: PropTypes.bool,
  customError: PropTypes.string,
};

FormGroupField.defaultProps = {
  type: 'text',
  floatingLabel: false,
};

export default observer(FormGroupField);
